import * as Sentry from "@sentry/react";
import { CURRENT_USER } from "Constants";
import { User } from "Models/user";
import env from "Util/env";
import { createBrowserHistory } from 'history';
import packageJson from "../../package.json";
import { AxiosError } from "axios";

const allowedDatabaseEnvironments = ["production", "test"]
const allowedBuildEnvironments = ["production"]
const isAllowedDatabaseEnvironment = allowedDatabaseEnvironments.includes(process.env.NODE_DATABASE as string)
const isAllowedBuildEnvironment = allowedBuildEnvironments.includes(process.env.NODE_ENV as string)
const isValidEnvironment = isAllowedDatabaseEnvironment && isAllowedBuildEnvironment

export const setupSentryUser = (userPayload?: User) => {
  console.log("[setupSentryUser] userPayload:", userPayload);
  if (!userPayload) return Sentry.setUser(null)

  const fullName = userPayload?.fullName ?? `${userPayload.name}${userPayload.lastName}`
  Sentry.setUser({
    username: userPayload.username,
    fullName: fullName,
    email: userPayload.email,
    id: userPayload.id,
    role: userPayload.role?.name,
    roleId: userPayload.roleId,
    company: userPayload.company?.name,
    companyId: userPayload.companyId,
  });
}

const getUserFromLocalStorage = () => {
  const currentUserJson = localStorage.getItem(CURRENT_USER);
  return currentUserJson ? JSON.parse(currentUserJson) : null
}

export const setupSentry = () => {
  const history = createBrowserHistory();
  console.log("[setupSentry] Initializing Sentry", {
    enabled: isValidEnvironment,
  })

  // Initialize Sentry
  Sentry.init({
    release: packageJson.version,
    dsn: env.SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({
        history
      }),
      Sentry.replayIntegration({
        maskAllText: false,
      })
    ],
    // Session Replay
    replaysSessionSampleRate: 0, // This sets the sample rate at 0%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      "https://test.shipeezi.com",
      "https://app.shipeezi.com",
      /^https:\/\/.*\.shipeezi\.com$/
    ],
    environment: process.env.NODE_DATABASE,
    enabled: isValidEnvironment,
  });
}



export const pushErrorToSentry = (
  errorName: string,
  extKeys?: { key: string, value: string }[],
  contexts?: { name: string, context: { key: string, value: string } }[]
) => {
  if (extKeys && extKeys?.length > 0) {
    extKeys?.forEach(extKey => Sentry.setExtra(extKey?.key, extKey?.value));
  }
  if (contexts && contexts?.length > 0) {
    contexts?.forEach(context => Sentry.setContext(context?.name, context?.context));
  }
  Sentry.captureException(new Error(errorName));
}
export function captureAxiosError(error: AxiosError) {
  error.name = (error.response?.data as {message:string}).message ?? error.message ?? error.name
  Sentry.captureException(error, {
    extra: {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      response: error.response?.data,
    },
  });
}
